import "@assets/css/bootstrap-grid.css";
import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint, getOptions } from "@services/api.services";
import { METHODS, SelectProps } from "@services/interface.service";
import {
  QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { SkeletonTwo } from "@ui/external/components/skeleton";
import { SortableTable } from "@ui/internal/components/table";
import { getInputField } from "@utils/function/index.function";
import { showToast, throwErrorAlert } from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { FormField } from "@utils/interface/form.interface";
import {
  certificateFormData,
  certificateSchema,
  StudentFormData,
  StudentFormSchema,
} from "@utils/interface/form.schema";
import {
  IndividualQuery,
  QueryMetaData,
} from "@utils/interface/index.interface";
import {
  BatchAmountData,
  StudentData,
} from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { usePending, useSelectionStore } from "@utils/states/index.state";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { Skeleton } from "@components/ui/skeleton";
import { Spinner } from "@components/ui/spinner";
import axiosInstance from "@services/index.services";
import React, { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useSearchParams } from "react-router";
import { CoursesColumnDetails } from "../column/index.column";
const StudentForm: React.FC = () => {
  const { handleAction } = useHandleAction({
    endpoint: endpoint[""],
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const pageItem = searchParams.get("page-item");
  const pageMode = searchParams.get("page");
  let {
    handleSubmit,
    control,
    reset,
    watch,
    getValues,

    setValue,
    formState: { errors },
  } = useForm<StudentFormData>({
    mode: "onChange",
    resolver: zodResolver(StudentFormSchema),
  });
  const { setIsFormPending } = useSelectionStore();

  const { data: fetchData, isPending: fetchPending } = useListData<
    IndividualQuery<StudentData>
  >({
    endpoint: endpoint["student"],
    key: `${pathname}-${pageItem}`,
    enabled: pageItem ? true : false,
    id: pageItem || "",
    window: false,
  });

  /* set default value */
  const { setIsRetrive }: any = usePending();

  useEffect(() => {
    setIsRetrive(pageItem ? fetchPending : false);
    if (fetchData?.status === 200 && !fetchPending) {
      const firstResult = fetchData?.data;
      console.log(firstResult);
      if (firstResult) {
        if (pageMode == "add") {
          return reset({});
        } else {
          reset({
            professional_image: firstResult?.professional_image,
            course_batch:
              firstResult?.course_batch?.map((item: any) => ({
                value: item.id, // This is the course_batch ID (45, 46, 47, 48)
                label: item.name, // This is the course_batch name
              })) || [],
            current_position: firstResult?.current_position || "",
            email: firstResult?.email || "",
            first_name: firstResult?.first_name || "",
            last_name: firstResult?.last_name || "",
            phone: firstResult?.user?.phone || "",
          });
        }
      }
    }
  }, [fetchData?.data, fetchPending]);

  const handleMutate = (data: any) => {
    setIsFormPending(true);

    if (pageItem) {
      data = {
        ...data,
        id: pageItem,
      };
    }

    data = {
      ...data,
      course_batch: data.course_batch?.map((item: SelectProps) => item.value),
      first_name: data.first_name,
      last_name: data.last_name,
      email: data?.email,
      professional_image: data?.professional_image,
      phone: data?.phone,
    };

    return {
      data,
      endpoint: endpoint["student"],
      method: pageItem ? METHODS.PUT : METHODS.POST,
    };
  };
  const handleSuccess = async ({
    status,
  }: {
    data: StudentData;
    status: number;
  }) => {
    setIsFormPending(false);
    if ([200, 201].includes(status)) {
      setIsFormPending(false);
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      showToast({
        description: `Student ${status === 200 ? "Updated" : "Created"}`,
        type: "success",
        title: "Success",
      });

      handleAction({
        type: "page-close",
      });
    } else {
      return new Error("Unknown API Error");
    }
  };
  console.log("errors", errors);
  const handleError = async (error: any) => {
    console.log(error.data.data, "error00");
    setIsFormPending(false);
    if (error?.data?.status == 413) {
      showToast({
        description: "File Size Too Large",
        type: "error",
        title: "Error",
      });
    }

    throwErrorAlert(error.data.data);
  };

  const { mutate } = usePostData();
  const { data: studentBatchData, refetch } = useListData<
    QueryMetaData<BatchAmountData, BatchAmountData>
  >({
    endpoint: endpoint["student-batch-amounts"],
    key: "student-batch-amounts",
    params: {
      student_id: searchParams.get("page-item"),
    },
    enabled: !!searchParams.get("page-item"),
  });

  /** =========================================== Invoice Course List api ==================================================== **/

  // const {} = useListData({
  //   endpoint: endpoi
  // })

  /** =============================================================================================== **/

  const onSubmit: SubmitHandler<StudentFormData> = (data) => {
    //Add Mutation Data
    const updatedData = Object.fromEntries(
      Object.entries(data).filter(
        ([key, value]) => key !== "professional_image" || value != null
      )
    );

    mutate(handleMutate(updatedData), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };
  const removeCourseBatch = async (data: any) => {
    const response = await axiosInstance.post(
      "/student/remove-coursebatch/",
      data,
      getOptions()
    );
    return response.data;
  };
  const { mutate: deleteMutate, isPending: courseDelPending } = useMutation({
    mutationFn: removeCourseBatch,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
      });
    },
    onError: (data) => handleError({ data }),
  });
  console.log("studentBatac", getValues("course_batch"));
  const columns = CoursesColumnDetails({
    studentId: searchParams.get("page-item") || "#",
  });

  const formSchema: FormField[] = [
    {
      name: "first_name",
      label: "First Name",
      colSpan: 6,
      type: "text",
      placeholder: "Enter First name",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "last_name",
      label: "Last Name",
      colSpan: 6,
      type: "text",
      placeholder: "Enter last name",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "email",
      label: "Email (Optional)",
      colSpan: 6,
      type: "text",
      placeholder: "Enter Email",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "phone",
      label: "Phone Number (Optional)",
      colSpan: 6,
      type: "tel",
      placeholder: "Enter Phone Number",
      control: control,
      setValue: setValue,
      errors,
      validation: { required: false },
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "course_batch",
      label: "Add Course & Batch",
      colSpan: 12,
      type: "async",
      endpoint: searchParams.get("page-item")
        ? endpoint["unique-course-batch-student"]
        : endpoint["course-batch"],
      format: { key: "name", value: "id" },
      filter: searchParams.get("page-item")
        ? {
            student_id: searchParams.get("page-item"),
          }
        : {},

      isMulti: true,
      disableOptionsForAsync: fetchData?.data?.course_batch?.map(
        (item) => item?.id
      ),
      onRemove: (item: SelectProps) => {
        searchParams.set("course_id", String(item.value));
        setSearchParams(searchParams);
        return false;
      },
      placeholder: "Select Batch",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      type: "component",
      colSpan: 12,
      visable: true,
      component: (
        <p className="text-[12px] text-[#969696] mb-[20px] -mt-[12px]">
          Picking another batch of a course this student already took counts as a
          repeat — no new invoice is generated for it.
        </p>
      ),
    },

    {
      name: "professional_image",
      label: "Upload Image",
      colSpan: 12,
      type: "cropper",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      type: "component",
      visable:
        pageMode == "edit" &&
        studentBatchData?.data &&
        studentBatchData?.data?.results.length > 0 &&
        !!searchParams.get("page-item"),

      component: (
        <div className="">
          <h4 className="font-bold ">Enrolled Courses</h4>

          <SortableTable
            endpoint={"/"}
            columns={columns ?? []}
            rows={studentBatchData?.data?.results ?? []}
          />
        </div>
      ),
    },
  ];
  console.log("studentBatchData", studentBatchData);
  const handleDelete = () => {
    const courseBatchIdToDelete = searchParams.get("course_id");

    deleteMutate(
      {
        student_id: searchParams.get("page-item"),
        course_batch_ids: [courseBatchIdToDelete],
      },
      {
        onSuccess: () => {
          // Remove the deleted course batch from the form field
          const currentCourseBatches = getValues("course_batch") || [];
          const updatedCourseBatches = currentCourseBatches.filter(
            (batch: any) => batch.value != courseBatchIdToDelete // Use != for loose comparison or convert types
          );

          console.log("Before filter:", currentCourseBatches);
          console.log(
            "ID to delete:",
            courseBatchIdToDelete,
            typeof courseBatchIdToDelete
          );
          console.log("After filter:", updatedCourseBatches);

          // Update the form field value
          setValue("course_batch", updatedCourseBatches, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
          });

          // Clean up URL params and refetch data
          searchParams.delete("course_id");
          setSearchParams(searchParams);
          queryClient.invalidateQueries({
            queryKey: [`${pathname}-${pageItem}`],
          });
          refetch();
        },
      }
    );
  };
  if (fetchPending && pageItem)
    return (
      <>
        <SkeletonTwo />
      </>
    );

  return (
    <>
      <form
        id={pathname}
        onSubmit={handleSubmit(onSubmit)}
        className="row w-full"
      >
        <div className="col-lg-8">
          <div className="row">
            {formSchema.slice(0, 6).map(
              (field, index) =>
                (field.visable || field.visable == undefined) && (
                  <div
                    key={index}
                    className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
                  >
                    {getInputField({ ...field })}
                  </div>
                )
            )}
          </div>
        </div>
        <div className="col-lg-4">
          <div className="row">
            {formSchema.slice(6, 7).map(
              (field, index) =>
                (field.visable || field.visable == undefined) && (
                  <div
                    key={index}
                    className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
                  >
                    {getInputField({ ...field })}
                  </div>
                )
            )}
          </div>
        </div>
        {formSchema.slice(7, 10).map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
              >
                {getInputField({ ...field })}
              </div>
            )
        )}
      </form>
      <AlertDialog open={!!searchParams.get("course_id")}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the selected course and batch from this student.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              type="button"
              onClick={() => {
                searchParams.delete("course_id");
                setSearchParams(searchParams);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={!!courseDelPending}
              type="button"
              onClick={() => {
                handleDelete();
              }}
            >
              {courseDelPending ? "Processing" : "Continue"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

const GenerateCertificateForm: React.FC<{ refetch?: () => void }> = ({
  refetch,
}) => {
  const { searchParams, handleAction } = useHandleAction({});
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const modalItem = searchParams.get("mode-add-item");
  const modalMode = searchParams.get("mode");
  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<certificateFormData>({
    mode: "onChange",
    resolver: zodResolver(certificateSchema),
  });

  const { data: fetchData, isPending: fetchPending } = useListData<
    IndividualQuery<StudentData>
  >({
    endpoint: endpoint["student"],
    key: `${pathname}-${modalItem}`,
    enabled: modalItem ? true : false,
    id: modalItem || "",
    window: false,
  });
  console.log("FetchDat", fetchData);
  /* set default value */
  const { setIsRetrive }: any = usePending();

  useEffect(() => {
    setIsRetrive(modalItem ? fetchPending : false);
    if (fetchData?.status === 200 && !fetchPending) {
      const firstResult = fetchData?.data;
      console.log(firstResult);
      if (firstResult) {
        if (modalMode == "add") {
          return reset({});
        } else {
          return reset({
            first_name: fetchData?.data?.first_name,
            last_name: fetchData?.data?.last_name,
            email: fetchData?.data?.email,
            phone: fetchData?.data?.user.phone,
            student: Number(modalItem),
          });
        }
      }
    }
  }, [fetchData?.data, fetchPending]);

  const handleMutate = (
    data: any,
    actionType: "save-only" | "save-and-send" = "save-and-send"
  ) => {
    // data = {...data ,course_enrolled:data["course_enrolled"]?.map((item:SelectProps)=> item.value)}
    // if (modalItem) {
    //   data = {
    //     ...data,
    //     id: modalItem,
    //   };
    //   if (!(data.professional_image instanceof File || data.professional_image instanceof Blob))
    //     delete data.professional_image;
    // }
    //         data = {
    //   "user": {
    //     "first_name": data.first_name,
    //     "last_name": data.last_name,
    //     "email": data?.email,
    //     "professional_image": data?.professional_image,
    //   },
    //   "phone_number": data?.phone_number,
    //   // "current_position": data?.current_position,
    //   "course_enrolled": data?.course_enrolled,
    // }

    data = {
      ...data,
      course_batch: data["course_batch"]?.[0]?.["value"],
      certificate_template: data["certificate_template"]?.[0]?.["value"],
    };

    return {
      data,
      endpoint:
        actionType === "save-only"
          ? endpoint["certificate-save-only"]
          : endpoint["certificate-save-and-send"],
      method: METHODS.POST,
    };
  };
  console.log("PATHNAME", pathname);
  const handleSuccess = async ({
    status,
  }: {
    data: StudentData;
    status: number;
  }) => {
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({
        queryKey: [`/certificate/pending-course-batches/ -async`],
      });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing`],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
        refetchType: "all",
      });
      /* the listing owns the `all_certificates_created` flag, so pull it again
         directly instead of relying on the invalidation alone */
      refetch?.();

      showToast({
        description: `Student Certificate ${
          status === 200 ? "Updated" : "Created"
        }`,
        type: "success",
        title: "Success",
      });

      handleAction({
        type: "mode-close",
      });
    } else {
      return new Error("Unknown API Error");
    }
  };
  const { setIsFormPending } = useSelectionStore();
  const handleError = async (error: any) => {
    console.log(error.data.data, "error00");
    setIsFormPending(false);
    if (error?.data?.status == 413) {
      showToast({
        description: "File Size Too Large",
        type: "error",
        title: "Error",
      });
    }

    throwErrorAlert(error.data.data);
  };

  const { mutate, isPending } = usePostData();
  console.log("errors", errors);

  const onSubmit: SubmitHandler<certificateFormData> = (data) => {
    //Add Mutation Data
    console.log("data", data);
    mutate(handleMutate(data, "save-and-send"), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };

  const onSaveOnly: SubmitHandler<certificateFormData> = (data) => {
    mutate(handleMutate(data, "save-only"), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };

  const formSchema: FormField[] = [
    {
      name: "course_batch",
      label: "Generate Certificate for Course:",
      colSpan: 12,
      type: "async",
      endpoint: fetchData?.data?.id
        ? endpoint["pending-course-batches"]
        : endpoint["course-batch"],
      format: { key: "name", value: "id" },
      // isMulti:true,
      placeholder: "Enter Course & Batch",
      control: control,
      filter: fetchData?.data?.id
        ? { student_id: fetchData.data.id }
        : undefined,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "certificate_template",
      label: "Certificate Template",
      colSpan: 12,
      type: "async",
      disabled: watch("course_batch") ? false : true,
      endpoint: endpoint["templates"],
      format: { key: "title", value: "id" },
      placeholder: "Enter Certificate Template",
      control: control,
      setValue: setValue,
      filter: watch("course_batch")?.length
        ? { course_batch__id: watch("course_batch")[0]?.["value"] }
        : {},
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      type: "component",
      colSpan: 12,
      component: <p className="text-[18px] font-[600]">Student Details</p>,
    },
    {
      name: "first_name",
      label: "First Name",
      colSpan: 6,
      disabled: true,
      isPending: fetchPending && modalMode !== "edit",
      type: "text",
      placeholder: "Enter Firstname",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "last_name",
      label: "Last Name",
      colSpan: 6,
      disabled: true,
      isPending: fetchPending && modalMode !== "edit",
      type: "text",
      placeholder: "Enter lastname",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "phone_number",
      label: "Phone Number",
      disabled: true,
      isPending: fetchPending && modalMode !== "edit",
      colSpan: 6,
      type: "tel",
      placeholder: "Enter Phone Number",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "email",
      label: "Email",
      disabled: true,
      colSpan: 6,
      type: "text",
      isPending: fetchPending && modalMode !== "edit",
      placeholder: "Enter Email",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "enrollment_date",
      label: "Start Date",
      colSpan: 6,
      type: "date",

      placeholder: "Enter Start Date",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "completion_date",
      label: "End Date",
      colSpan: 6,
      type: "date",
      id: "guras_nani2",
      placeholder: "Enter End Date",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "remarks",
      label: "Remarks",
      colSpan: 12,
      type: "textarea",
      placeholder: "Enter Description",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full border rounded-md w-full`,
      visable: true,
    },
    {
      name: "glimpse",
      label: "Glimpse",
      colSpan: 12,
      type: "cropper",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      type: "submit",
      value: `Save & Send Certificate`,
      className: "w-full h-[47px] mt-[32px] capitalize",
      colSpan: 6,
      variant: "default",
      endIcon: isPending ? <Spinner /> : null,
      disabled: isPending ? true : false,
      visable: true,
    },
    {
      type: "button",
      value: `Save Only`,
      className: "w-full h-[47px] mt-[32px] capitalize",
      colSpan: 6,
      variant: "outline",
      endIcon: isPending ? <Spinner /> : null,
      disabled: isPending ? true : false,
      onClick: () => handleSubmit(onSaveOnly)(),
      visable: true,
    },
  ];

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="row w-full max-h-[80vh] overflow-y-scroll"
      >
        {formSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
              >
                {field.isPending ? (
                  <Skeleton className={cn(["h-[70px] w-full mb-[12px]"])} />
                ) : (
                  getInputField({ ...field })
                )}
              </div>
            )
        )}
      </form>
    </>
  );
};

// const StudentImportForm = () => {
//   const { searchParams, handleAction } = useHandleAction({});
//   const pathname = usePathname();
//   const queryClient = useQueryClient();
//   const pageItem = searchParams.get("page-item");
//   const pageMode = searchParams.get("page");
//   const {
//     handleSubmit,
//     control,
//     reset,
//     watch,

//     setValue,
//     formState: { errors },
//   } = useForm<StudentFormData>({
//     mode: "onChange",
//     resolver: zodResolver(StudentFormSchema),
//   });
//   const { setIsFormPending } = useSelectionStore();
//   const [availableColumns, setAvailableColumns] = React.useState<any>([]);

//   const { mutate: uploadImageMutation } = usePostData();

//   const [file, setFile] = React.useState<File | undefined>();
//   const uploadImage = (fileData: File) => {
//     // console.log(fileData?.size > 5 * 1024 * 1024, "fileData");
//     // if (fileData?.size > 5242880) {
//     //   toast.error("File must be less than 5MB and in CSV or XLSV format");
//     //   return;
//     // }
//     setFile(fileData);

//     uploadImageMutation(
//       {
//         endpoint: endpoint["import-excel-student"],
//         method: METHODS.POST,
//         data: {
//           excel_file: file ?? fileData,
//         },
//       },
//       {
//         onSuccess: ({ data, status }) => {
//           if ([200, 201].includes(status)) {
//             console.log("data", data);
//             toast.success("File uploaded successfully");
//             setAvailableColumns(data?.available_columns);
//           } else {
//             toast.error(data?.error || "Failed to upload image");
//           }
//         },
//       }
//     );
//   };
//   const formSchema: FormField[] = [
//     {
//       type: "component",
//       colSpan: 12,
//       label: "Import File",
//       component: (
//         <>
//           <Controller
//             name="import_file"
//             control={control}
//             render={({ onChange, value }) => (
//               <input
//                 type="file"
//                 accept=" .xlsx"
//                 onChange={(e) => uploadImage(e.target.files?.[0])}
//                 value={value}
//               />
//             )}
//           />
//         </>
//       ),
//     },
//     {
//       name: "full_name",
//       label: "Full Name",
//       colSpan: 12,
//       type: "text",
//       placeholder: "Enter Full name",
//       control: control,
//       setValue: setValue,
//       errors,
//       className: `mb-[20px] min-w-full w-full`,
//       visable: true,
//     },

//     {
//       name: "email",
//       label: "Email",
//       colSpan: 12,
//       type: "text",
//       placeholder: "Enter Email",
//       control: control,
//       setValue: setValue,
//       errors,
//       className: `mb-[20px] min-w-full w-full`,
//       visable: true,
//     },
//     {
//       name: "phone",
//       label: "Phone Number",
//       colSpan: 12,
//       type: "tel",
//       placeholder: "Enter Phone Number",
//       control: control,
//       setValue: setValue,
//       errors,
//       className: `mb-[20px] min-w-full w-full`,
//       visable: true,
//     },
//   ];
//   const onSubmit = () => {};
//   return (
//     <>
//       <form
//         id={pathname}
//         onSubmit={handleSubmit(onSubmit)}
//         className="row w-full"
//       >
//         <div className="col-lg-12">
//           <div className="row">
//             {formSchema.map(
//               (field, index) =>
//                 (field.visable || field.visable == undefined) && (
//                   <div
//                     key={index}
//                     className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
//                   >
//                     {getInputField({ ...field })}
//                   </div>
//                 )
//             )}
//           </div>
//         </div>
//       </form>
//     </>
//   );
// };
export default StudentForm;
export { GenerateCertificateForm };
// StudentImportForm };
